import { supabase } from '../supabase/client';
import { 
  Invitation, 
  CreateInvitationRequest, 
  BulkCreateInvitationRequest,
  AcceptInvitationRequest
} from '@/types/invitation';
import { sendInvitationEmail } from '@/lib/email';

export const invitationService = {
  /**
   * Create a new invitation
   */
  async createInvitation(data: CreateInvitationRequest): Promise<Invitation> {
    if (!supabase) throw new Error('Supabase client not initialized');
    
    // Get current user ID
    const { data: userData } = await supabase.auth.getUser();
    if (!userData || !userData.user) {
      throw new Error('You must be logged in to send invitations');
    }

    const { data: invitation, error } = await supabase
      .from('invitations')
      .insert({
        team_id: data.team_id,
        email: data.email.toLowerCase(),
        role: data.role,
        shift_type: data.shift_type,
        invited_by: userData.user.id
      })
      .select(`
        *,
        team:team_id (
          name
        ),
        invited_by_user:invited_by (
          full_name,
          email
        )
      `)
      .single();

    if (error) {
      throw new Error(`Failed to create invitation: ${error.message}`);
    }

    // Send invitation email
    try {
      await sendInvitationEmail(
        invitation.email, 
        '', // Recipient name (might be unknown)
        invitation.team?.name || 'a team', 
        invitation.invited_by_user?.full_name || 'A team member',
        invitation.token
      );
    } catch (emailError) {
      console.error('Failed to send invitation email:', emailError);
      // Don't throw error here as we still want to return the created invitation
    }

    return invitation;
  },

  /**
   * Create multiple invitations at once
   */
  async bulkCreateInvitations(data: BulkCreateInvitationRequest): Promise<Invitation[]> {
    if (!supabase) throw new Error('Supabase client not initialized');
    
    // Get current user ID
    const { data: userData } = await supabase.auth.getUser();
    if (!userData || !userData.user) {
      throw new Error('You must be logged in to send invitations');
    }
    
    const invitationsToCreate = data.invitations.map(inv => ({
      team_id: data.team_id,
      email: inv.email.toLowerCase(),
      role: inv.role,
      shift_type: inv.shift_type,
      invited_by: userData.user.id
    }));

    const { data: invitations, error } = await supabase
      .from('invitations')
      .insert(invitationsToCreate)
      .select(`
        *,
        team:team_id (
          name
        ),
        invited_by_user:invited_by (
          full_name,
          email
        )
      `);

    if (error) {
      throw new Error(`Failed to create invitations: ${error.message}`);
    }

    // Get team name for emails
    const teamName = invitations?.[0]?.team?.name || 'a team';
    const inviterName = invitations?.[0]?.invited_by_user?.full_name || 'A team member';
    
    // Send invitation emails
    if (invitations && invitations.length > 0) {
      for (const invitation of invitations) {
        try {
          await sendInvitationEmail(
            invitation.email,
            '', // Recipient name (might be unknown)
            teamName,
            inviterName,
            invitation.token
          );
        } catch (emailError) {
          console.error(`Failed to send invitation email to ${invitation.email}:`, emailError);
          // Continue with other emails even if one fails
        }
      }
    }

    return invitations || [];
  },

  /**
   * Get all pending invitations for a team
   */
  async getTeamInvitations(teamId: string): Promise<Invitation[]> {
    if (!supabase) throw new Error('Supabase client not initialized');
    
    const { data: invitations, error } = await supabase
      .from('invitations')
      .select(`
        *,
        team:team_id (
          name
        ),
        invited_by_user:invited_by (
          full_name,
          email
        )
      `)
      .eq('team_id', teamId)
      .eq('status', 'pending');

    if (error) {
      throw new Error(`Failed to fetch invitations: ${error.message}`);
    }

    return invitations || [];
  },

  /**
   * Get invitations for the current user
   */
  async getUserInvitations(): Promise<Invitation[]> {
    if (!supabase) throw new Error('Supabase client not initialized');
    
    const { data: user } = await supabase.auth.getUser();
    
    if (!user || !user.user) {
      return [];
    }

    const { data: invitations, error } = await supabase
      .from('invitations')
      .select(`
        *,
        team:team_id (
          name
        ),
        invited_by_user:invited_by (
          full_name,
          email
        )
      `)
      .eq('email', user.user.email?.toLowerCase())
      .eq('status', 'pending');

    if (error) {
      throw new Error(`Failed to fetch invitations: ${error.message}`);
    }

    return invitations || [];
  },

  /**
   * Get an invitation by token
   */
  async getInvitationByToken(token: string): Promise<Invitation> {
    if (!supabase) throw new Error('Supabase client not initialized');
    
    try {
      console.log('Attempting to fetch invitation with token:', token);
      
      // Try SDK approach first since authenticated session methods work better through SDK
      if (supabase.auth) {
        const { data: session } = await supabase.auth.getSession();
        const isAuth = !!session?.session;
        
        console.log('User authenticated status:', isAuth);
        
        if (isAuth) {
          // If authenticated, try SDK first - more likely to work
          try {
            console.log('Using SDK to fetch invitation (authenticated)');
            return await this.getInvitationByTokenSDK(token);
          } catch (sdkError) {
            console.error('SDK fetch failed, trying REST:', sdkError);
            // SDK failed, try REST approach
          }
        }
      }
      
      // If not authenticated or SDK failed, try REST API approach
      return await this.getInvitationByTokenREST(token);
    } catch (err) {
      // Log the error for debugging
      console.error('All invitation fetch methods failed:', err);
      
      // Re-throw as a user-friendly message
      if (err instanceof Error) {
        throw err;
      }
      
      throw new Error('Failed to load invitation. Please try again later.');
    }
  },
  
  /**
   * Use Supabase SDK to get invitation by token
   */
  async getInvitationByTokenSDK(token: string): Promise<Invitation> {
    if (!supabase) throw new Error('Supabase client not initialized');
    
    console.log('Fetching invitation with SDK, token:', token);
    
    const { data: invitation, error } = await supabase
      .from('invitations')
      .select(`
        *,
        team:team_id (
          name
        ),
        invited_by_user:invited_by (
          full_name,
          email
        )
      `)
      .eq('token', token)
      .single();
    
    if (error) {
      console.error('SDK invitation fetch error:', error);
      throw new Error(`Invalid or expired invitation: ${error.message}`);
    }
    
    console.log('SDK invitation fetch success:', invitation);
    return invitation;
  },
  
  /**
   * Use direct REST API to get invitation by token
   */
  async getInvitationByTokenREST(token: string): Promise<Invitation> {
    console.log('Fetching invitation with REST API, token:', token);
    
    const supabaseUrl = import.meta.env.VITE_SUPABASE_URL as string;
    const supabaseKey = import.meta.env.VITE_SUPABASE_ANON_KEY as string;
    
    // Properly encode the token for the URL
    const encodedToken = encodeURIComponent(token);
    
    // Use simpler URL structure with clearer parameter encoding
    const url = new URL(`${supabaseUrl}/rest/v1/invitations`);
    url.searchParams.append('token', `eq.${encodedToken}`);
    url.searchParams.append('select', '*,team:team_id(name),invited_by_user:invited_by(full_name,email)');
    
    console.log('REST API URL:', url.toString());
    
    const response = await fetch(url.toString(), {
      method: 'GET',
      headers: {
        'apikey': supabaseKey,
        'Authorization': `Bearer ${supabaseKey}`,
        'Accept': 'application/json',
        'Content-Type': 'application/json',
        'Prefer': 'return=representation'
      }
    });
    
    console.log('REST API response status:', response.status, response.statusText);
    
    if (!response.ok) {
      throw new Error(`Failed to fetch invitation: ${response.statusText}`);
    }
    
    const data = await response.json();
    console.log('REST API response data:', data);
    
    if (!data || !Array.isArray(data) || data.length === 0) {
      throw new Error('Invitation not found or has expired.');
    }
    
    return data[0] as Invitation;
  },

  /**
   * Accept an invitation
   */
  async acceptInvitation(data: AcceptInvitationRequest): Promise<void> {
    if (!supabase) throw new Error('Supabase client not initialized');
    
    try {
      // First, get the invitation
      const invitation = await this.getInvitationByToken(data.token);

      // Check if it's still pending
      if (invitation.status !== 'pending') {
        throw new Error('This invitation has already been processed.');
      }

      // Check if it's expired
      if (new Date(invitation.expires_at) < new Date()) {
        throw new Error('This invitation has expired.');
      }

      // Get current user
      const { data: userData } = await supabase.auth.getUser();
      if (!userData || !userData.user) {
        throw new Error('You must be logged in to accept an invitation.');
      }

      // Begin a transaction by using a single supabase call with multiple
      // operations that will succeed or fail together
      const { error } = await supabase.rpc('accept_invitation', {
        invitation_token: data.token,
        member_user_id: userData.user.id
      });

      if (error) {
        throw new Error(`Failed to accept invitation: ${error.message}`);
      }
    } catch (err) {
      console.error('Error accepting invitation:', err);
      if (err instanceof Error) {
        throw err;
      }
      throw new Error('Failed to accept the invitation. Please try again later.');
    }
  },

  /**
   * Decline an invitation
   */
  async declineInvitation(token: string): Promise<void> {
    if (!supabase) throw new Error('Supabase client not initialized');
    
    try {
      // Get the invitation first to verify it exists and is valid
      await this.getInvitationByToken(token);
      
      const { error } = await supabase
        .from('invitations')
        .update({ status: 'declined' })
        .eq('token', token);

      if (error) {
        throw new Error(`Failed to decline invitation: ${error.message}`);
      }
    } catch (err) {
      console.error('Error declining invitation:', err);
      if (err instanceof Error) {
        throw err;
      }
      throw new Error('Failed to decline the invitation. Please try again later.');
    }
  },

  /**
   * Resend an invitation
   */
  async resendInvitation(invitationId: string): Promise<Invitation> {
    if (!supabase) throw new Error('Supabase client not initialized');
    
    const { data: invitation, error } = await supabase
      .rpc('resend_invitation', {
        invitation_id: invitationId
      })
      .single();

    if (error) {
      throw new Error(`Failed to resend invitation: ${error.message}`);
    }

    // Cast the invitation to the correct type
    const typedInvitation = invitation as Invitation;

    // Send invitation email
    try {
      await sendInvitationEmail(
        typedInvitation.email, 
        '', // Recipient name (might be unknown)
        typedInvitation.team?.name || 'a team', 
        typedInvitation.invited_by_user?.full_name || 'A team member',
        typedInvitation.token
      );
    } catch (emailError) {
      console.error('Failed to send invitation email:', emailError);
      // Don't throw error here as we still want to return the updated invitation
    }

    return typedInvitation;
  },

  /**
   * Cancel an invitation
   */
  async cancelInvitation(invitationId: string): Promise<void> {
    if (!supabase) throw new Error('Supabase client not initialized');
    
    const { error } = await supabase
      .from('invitations')
      .delete()
      .eq('id', invitationId);

    if (error) {
      throw new Error(`Failed to cancel invitation: ${error.message}`);
    }
  }
}; 